import React, { useState, useEffect } from "react";
import SocialActions from '../../components/SocialActions/SocialActions';
import bibleContentData from '../../assets/data/bible-content.json';
import bibleQuotes from '../../assets/data/quotes.json';

// Books 1-54 = Old Testament (ብሉይ ኪዳን), 55-81 = New Testament (ሐዲስ ኪዳን) — Matthew starts at 55
const OLD_TESTAMENT = bibleContentData.filter(b => b.book_number <= 54);
const NEW_TESTAMENT = bibleContentData.filter(b => b.book_number >= 55);

const Bible = () => {
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [viewMode, setViewMode] = useState('interactive');
  const [testament, setTestament] = useState('old'); // 'old' | 'new'
  
  const bibleContent = bibleContentData;
  const filteredBooks = testament === 'old' ? OLD_TESTAMENT : NEW_TESTAMENT;
  const [selectedBook, setSelectedBook] = useState(OLD_TESTAMENT[0] || null);
  const [selectedChapter, setSelectedChapter] = useState(1);
  const [dailyQuote, setDailyQuote] = useState(null);

  useEffect(() => {
    const checkPdfAvailability = async () => {
      try {
        const response = await fetch('/bible.pdf', { method: 'HEAD' });
        if (response.ok) setPdfUrl('/bible.pdf');
      } catch (err) {
        console.error('PDF check error:', err);
      }
    };

    const loadQuotes = () => {
      if (Array.isArray(bibleQuotes) && bibleQuotes.length > 0) {
        const now = new Date();
        const start = new Date(now.getFullYear(), 0, 0);
        const dayOfYear = Math.floor((now - start) / (1000 * 60 * 60 * 24));
        setDailyQuote(bibleQuotes[dayOfYear % bibleQuotes.length]);
      }
    };

    checkPdfAvailability();
    loadQuotes();
    setLoading(false);
  }, []);

  const handleBookChange = (e) => {
    const bookId = e.target.value;
    const book = bibleContent.find(b => b.book_short_name_en === bookId || b.book_name_en === bookId);
    setSelectedBook(book);
    setSelectedChapter(1);
  };

  // When testament changes, reset to first book of that testament
  const handleTestamentChange = (t) => {
    setTestament(t);
    const books = t === 'old' ? OLD_TESTAMENT : NEW_TESTAMENT;
    setSelectedBook(books[0] || null);
    setSelectedChapter(1);
  };

  const handleChapterChange = (e) => setSelectedChapter(parseInt(e.target.value));
  const currentChapterData = selectedBook?.chapters.find(c => c.chapter === selectedChapter);

  return (
    <div className="page-container bible-theme">
      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-main-title">📖 መጽሐፍ ቅዱስ</h1>
        <div className="page-subtitle">Holy Bible - የኢትዮጵያ ኦርቶዶክስ ተዋህዶ ቤተክርስቲያን</div>
        <div className="page-description">
          <p className="page-description-main">የኢትዮጵያ ኦርቶዶክስ ተዋህዶ ቤተክርስቲያን አማርኛ ትርጉም</p>
          <p className="page-description-sub">Ethiopian Orthodox Tewahedo Church Amharic Translation</p>
        </div>
      </div>

      {/* Daily Quote */}
      {dailyQuote && (
        <div className="content-container mb-6">
          <div className="content-header">
            <div className="content-icon">❝</div>
            <h4 className="content-title text-sm uppercase tracking-wider">የዕለቱ ጥቅስ (Daily Verse)</h4>
          </div>
          <p className="text-xl font-medium italic mb-3 leading-relaxed">
            "{dailyQuote.text}"
          </p>
          <div className="flex items-center gap-2 text-blue-600 font-semibold">
            <span className="w-6 h-0.5 bg-blue-400 inline-block"></span>
            <span>{dailyQuote.author}</span>
          </div>
        </div>
      )}

      {/* View Mode Toggle */}
      <div className="controls-container">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          {/* Testament Filter */}
          <div className="flex bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => handleTestamentChange('old')}
              className={`px-4 py-1.5 rounded-lg transition-all duration-200 text-sm font-medium ${
                testament === 'old' ? 'bg-white text-blue-600 shadow-md' : 'text-gray-600 hover:text-blue-500'
              }`}
            >
              📜 ብሉይ ኪዳን
            </button>
            <button
              onClick={() => handleTestamentChange('new')}
              className={`px-4 py-1.5 rounded-lg transition-all duration-200 text-sm font-medium ${
                testament === 'new' ? 'bg-white text-purple-600 shadow-md' : 'text-gray-600 hover:text-purple-500'
              }`}
            >
              ✝️ ሐዲስ ኪዳን
            </button>
          </div>

          {/* View Mode */}
          <div className="flex bg-gray-100 p-1 rounded-xl">
            <button
              onClick={() => setViewMode('interactive')}
              className={`px-4 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium ${
                viewMode === 'interactive' ? 'bg-white text-blue-600 shadow-md' : 'text-gray-600 hover:text-blue-500'
              }`}
            >
              <span>📖</span> Interactive
            </button>
            <button
              onClick={() => setViewMode('pdf')}
              className={`px-4 py-1.5 rounded-lg transition-all duration-200 flex items-center gap-2 text-sm font-medium ${
                viewMode === 'pdf' ? 'bg-white text-blue-600 shadow-md' : 'text-gray-600 hover:text-blue-500'
              }`}
            >
              <span>📄</span> PDF
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'interactive' ? (
        <div className="space-y-6">
          {/* Book and Chapter Selector */}
          <div className="controls-container">
            <div className="controls-grid">
              <div className="control-group">
                <label className="control-label">መጽሐፍ ይምረጡ (Select Book):</label>
                <select
                  className="control-select"
                  value={selectedBook?.book_short_name_en || selectedBook?.book_name_en || ''}
                  onChange={handleBookChange}
                >
                  {filteredBooks.map(book => (
                    <option key={book.book_name_en} value={book.book_short_name_en || book.book_name_en}>
                      {book.book_name_am} ({book.book_name_en})
                    </option>
                  ))}
                </select>
              </div>
              <div className="control-group">
                <label className="control-label">ምዕራፍ ይምረጡ (Select Chapter):</label>
                <select
                  className="control-select"
                  value={selectedChapter}
                  onChange={handleChapterChange}
                >
                  {selectedBook?.chapters.map(ch => (
                    <option key={ch.chapter} value={ch.chapter}>ምዕራፍ {ch.chapter}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Verse Display */}
          {selectedBook && (
            <div className="content-container">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold mb-2">{selectedBook.book_name_am}</h2>
                <div className="w-20 h-1 bg-blue-500 mx-auto rounded-full mb-4"></div>
                <h3 className="text-xl text-blue-600 font-semibold">ምዕራፍ {selectedChapter}</h3>
              </div>

              <div className="space-y-4">
                {currentChapterData?.sections.map((section, sIndex) => (
                  <div key={sIndex}>
                    {section.title && (
                      <h4 className="text-lg font-bold mb-3 pb-2 border-b border-gray-200">
                        {section.title}
                      </h4>
                    )}
                    <div className="space-y-3">
                      {section.verses.map((verse, vIndex) => (
                        <div key={vIndex} className="flex gap-4 group">
                          <span className="text-blue-500 font-bold shrink-0 mt-1 text-sm">
                            {verse.verse}
                          </span>
                          <p className="text-lg leading-relaxed group-hover:text-blue-900 transition-colors">
                            {verse.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Navigation */}
              <div className="flex justify-between items-center mt-10 pt-6 border-t border-gray-200">
                <button
                  disabled={selectedChapter <= 1 && bibleContent.indexOf(selectedBook) === 0}
                  onClick={() => {
                    if (selectedChapter > 1) {
                      setSelectedChapter(prev => prev - 1);
                    } else {
                      const prevBook = bibleContent[bibleContent.indexOf(selectedBook) - 1];
                      setSelectedBook(prevBook);
                      setSelectedChapter(prevBook.chapters.length);
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="btn-secondary disabled:opacity-30"
                >
                  ⬅️ ቀዳሚ
                </button>
                <button
                  disabled={selectedChapter >= (selectedBook?.chapters.length || 0) && bibleContent.indexOf(selectedBook) === bibleContent.length - 1}
                  onClick={() => {
                    if (selectedChapter < (selectedBook?.chapters.length || 0)) {
                      setSelectedChapter(prev => prev + 1);
                    } else {
                      const nextBook = bibleContent[bibleContent.indexOf(selectedBook) + 1];
                      setSelectedBook(nextBook);
                      setSelectedChapter(1);
                    }
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className="btn-primary disabled:opacity-30"
                >
                  ቀጣይ ➡️
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {/* PDF Controls */}
          <div className="content-container">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="content-header mb-0">
                <div className="content-icon">📄</div>
                <div>
                  <h3 className="content-title text-lg">የመጽሐፍ ቅዱስ PDF</h3>
                  <p className="text-sm text-gray-600">Use browser controls to navigate, search, and zoom</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <a href="/bible.pdf" download="bible.pdf" className="btn-success">
                  📥 Download PDF
                </a>
                <a href="/bible.pdf" target="_blank" rel="noopener noreferrer" className="btn-primary">
                  🔗 Open in New Tab
                </a>
              </div>
            </div>
          </div>

          {loading && (
            <div className="content-container text-center">
              <div className="loading-spinner mx-auto mb-4"></div>
              <p className="text-blue-600 font-medium">መጽሐፍ ቅዱስ በመጫን ላይ...</p>
            </div>
          )}

          {error && (
            <div className="error-container text-center">
              <div className="text-4xl mb-4">❌</div>
              <p className="font-medium">PDF ፋይል መጫን አልተቻለም</p>
            </div>
          )}

          {!loading && !error && pdfUrl && (
            <div className="content-container overflow-hidden p-0">
              <iframe
                src={`${pdfUrl}#toolbar=1&navpanes=1&scrollbar=1`}
                width="100%"
                height="800"
                title="Holy Bible PDF"
                className="w-full"
                style={{ minHeight: '800px', border: 'none' }}
              />
            </div>
          )}
        </div>
      )}

      {/* Social Actions */}
      <div className="content-container mt-6">
        <div className="content-header">
          <div className="content-icon">📤</div>
          <h3 className="content-title">አጋራ እና ቀምጥ (Share & Bookmark)</h3>
        </div>
        <SocialActions
          contentId="bible_page"
          contentType="bible"
          title="መጽሐፍ ቅዱስ - Holy Bible"
          content="የኢትዮጵያ ኦርቶዶክስ ተዋህዶ ቤተክርስቲያን አማርኛ ትርጉም መጽሐፍ ቅዱስ"
          showBookmark={true}
          showShare={true}
          showLike={true}
        />
      </div>

      {/* Instructions */}
      <div className="content-container mt-6">
        <div className="content-header">
          <div className="content-icon">📋</div>
          <h3 className="content-title">የአጠቃቀም መመሪያ (Usage Instructions)</h3>
        </div>
        <div className="instructions-grid">
          <div className="instruction-card">
            <div className="instruction-header">
              <span className="instruction-icon">🔍</span>
              <h3 className="instruction-title">Navigation & Search</h3>
            </div>
            <p className="instruction-description">
              Use Ctrl+F to search within PDF. Use mouse wheel to scroll and click-drag to pan.
            </p>
          </div>
          <div className="instruction-card">
            <div className="instruction-header">
              <span className="instruction-icon">🔧</span>
              <h3 className="instruction-title">Controls</h3>
            </div>
            <p className="instruction-description">
              Ctrl+Plus/Minus to zoom. Use "Download PDF" to save locally or "Open in New Tab" for full-screen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Bible;
